import { ReactNode, useCallback } from 'react'

import {
  type FormFieldDescriptor,
  type NarrowFieldsByValueType,
  type TidalFormBridgeOptions,
  type TidalFormState,
  useTidalFormBridge,
} from '@workwave-tidal/core/form'

import { Box, FormHelperText } from '@mui/material'

import { OnRemovePOD } from '../typings'

export interface PodListItemFieldInputProps {
  testId?: string
  renderItems: (values: uui.domain.client.rm.Pod[], onRemove: OnRemovePOD) => ReactNode
}

export type PodListItemFieldValue = uui.domain.client.rm.Pod[]
const defaultComponentValue: PodListItemFieldValue = []

export type PodListItemFieldProps<
  TargetField extends NarrowFieldsByValueType<AcceptedFormValue, State, FormFieldDescriptor<State>>,
  State extends TidalFormState,
  AcceptedFormValue = PodListItemFieldValue,
> = {
  podListItemFieldInputProps: PodListItemFieldInputProps
} & TidalFormBridgeOptions<PodListItemFieldValue, TargetField, State, AcceptedFormValue>

export function PodListItemField<
  TargetField extends NarrowFieldsByValueType<AcceptedFormValue, State, FormFieldDescriptor<State>>,
  State extends TidalFormState,
  AcceptedFormValue = PodListItemFieldValue,
>(props: PodListItemFieldProps<TargetField, State, AcceptedFormValue>) {
  const {
    podListItemFieldInputProps: { testId, renderItems },
    initialValue = defaultComponentValue as FormFieldDescriptor<State>[TargetField]['value'],
    validateOn = 'change',
    ...bridgeOptions
  } = props

  const {
    fieldInfo: { value, onChange, errorOrHelperText, showErrorAccent, fieldHasError },
  } = useTidalFormBridge<PodListItemFieldValue, TargetField, State, AcceptedFormValue>({
    ...bridgeOptions,
    validateOn,
    initialValue,
  })

  const handleOnDelete = useCallback(
    (uuid: string) => {
      const updatedValues: uui.domain.client.rm.Pod[] = value.filter(
        ({ uuid: podId }: uui.domain.client.rm.Pod) => podId !== uuid,
      )

      onChange(updatedValues)
    },
    [onChange, value],
  )

  return (
    <Box data-testid={testId}>
      {renderItems(value, handleOnDelete)}
      {showErrorAccent && fieldHasError && (
        <FormHelperText error>{errorOrHelperText}</FormHelperText>
      )}
    </Box>
  )
}
